import {
  CLEAR_ERRORS,
  HIDE_WORK_ORDER_ADD_PRODUCT_MODAL,
  SHOW__WORK_ORDER_ADD_PRODUCT_MODAL,
} from "../../constants/types";
import backend from "../../services/axios.functions";
import { GenericRootThunkAction } from "../../types/common";
import { PAGES } from "../../features/navigation/types/pages.constant";
import { successMessage, errorMessage } from "./messagesActions";
import { handleError, createError } from "./errorHandlingAction";
import mapParameters from "../../utils/mapParameters";
import { RESULTS_PER_PAGE } from "../../utils/constants";
import { ApprovalStatus } from "../../types/approval-status/approval-status.type";
import { WorkOrderProps } from "../../pages/work-order/components/work-order/work-order.props";
import { LOADING_STATES } from "../../features/loading";
import { mapFormToParams } from "../../pages/work-order/types/work-order.functions";

export const FETCH_WORK_ORDER_FILTERS = "FETCH_WORK_ORDER_FILTERS";
export const FETCH_WORK_ORDER_FILTERS_SUCCESS = "FETCH_WORK_ORDER_FILTERS_SUCCESS";

export const SET_WORK_ORDER_DATA = "SET_WORK_ORDER_DATA";
export const SET_WORK_ORDER_ERRORS = "SET_WORK_ORDER_ERRORS";

// work-orders reducer types
export const SET_WORK_ORDER_LOADING = "SET_WORK_ORDER_LOADING";

export const SET_WORK_ORDER_STATUS = "SET_WORK_ORDER_STATUS";
export const SET_WORK_ORDER_PARENT_STATUS = "SET_WORK_ORDER_PARENT_STATUS";

export const LOADING_WORK_ORDERS = "LOADING_WORK_ORDERS";
export const LIST_WORK_ORDERS = "LIST_WORK_ORDERS";
export const WORK_ORDER_PAGE_CHANGE = "WORK_ORDER_PAGE_CHANGE";

export const SHOW_NEW_WORK_ORDER_MODAL = "SHOW_NEW_WORK_ORDER_MODAL";
export const HIDE_NEW_WORK_ORDER_MODAL = "HIDE_NEW_WORK_ORDER_MODAL";

export const CLEAR_WORK_ORDER_ERRORS = "CLEAR_WORK_ORDER_ERRORS";
export const RESET_WORK_ORDER_ATTRIBUTE = "RESET_WORK_ORDER_ATTRIBUTE";
export const RESET_WORK_ORDER = "RESET_WORK_ORDER";

// Success/Failure New WorkOrder Briefs
export const SUCCESS_NEW_WORK_ORDER_PAGE = "SUCCESS_NEW_WORK_ORDER_PAGE";
export const FAILURE_NEW_WORK_ORDER_PAGE = "FAILURE_NEW_WORK_ORDER_PAGE";

// Success/Failure Update WorkOrder Page
export const SUCCESS_UPDATE_WORK_ORDER_PAGE = "SUCCESS_UPDATE_WORK_ORDER_PAGE";
export const SUCCESS_RENAME_WORK_ORDER_PAGE = "SUCCESS_RENAME_WORK_ORDER_PAGE";
export const FAILURE_UPDATE_WORK_ORDER_PAGE = "FAILURE_UPDATE_WORK_ORDER_PAGE";

// Store Format options
export const SET_STORE_FORMATS_OPTIONS = "SET_STORE_FORMATS_OPTIONS";
export const SET_STORE_FORMATS_OPTIONS_ERRORS = "SET_STORE_FORMATS_OPTIONS_ERRORS";

// File Format options
export const SET_FILE_FORMAT_OPTIONS = "SET_FILE_FORMAT_OPTIONS";
export const SET_FILE_FORMAT_OPTIONS_ERRORS = "SET_FILE_FORMAT_OPTIONS_ERRORS";

export const getWorkOrderFilters = () => async (dispatch: any) => {
  dispatch({ type: FETCH_WORK_ORDER_FILTERS });
  try {
    const { data: payload } = await backend.get("/work-orders/filter-options");
    if (payload) {
      dispatch({
        type: FETCH_WORK_ORDER_FILTERS_SUCCESS,
        payload,
      });
    }
  } catch (err) {
    console.error(err);
  }
};

const paramMap = {
  assignee: "content_creators",
  creativePrime: "creative_primes",
  dateFrom: "due_at_after",
  dateTo: "due_at_before",
  status: "status",
  format: "format",
};

const defaultParams = {
  limit: [RESULTS_PER_PAGE],
  page: [1],
  ordering: ["due_at"],
};

export function getExportWorkOrderListUrl(params: any = {}): string {
  const queryString = mapParameters({ ...defaultParams, ...params, format: ["csv"] }, paramMap);

  return `/work-orders/${queryString}`;
}

export const getWorkOrderList = (params: any = {}, pageChange: boolean = false) => async (
  dispatch: any
) => {
  const queryString = mapParameters({ ...defaultParams, ...params }, paramMap);

  if (pageChange) {
    dispatch({ type: WORK_ORDER_PAGE_CHANGE });
  } else {
    dispatch({ type: LOADING_WORK_ORDERS });
  }

  try {
    const { data } = await backend.get(`/work-orders/${queryString}`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: LIST_WORK_ORDERS,
      payload: data,
    });
  } catch (error) {
    handleError(error, dispatch, SET_WORK_ORDER_ERRORS);
  }
};

export const getWorkOrderListInExecutionBrief = (
  executionBriefId: number
): GenericRootThunkAction => async (dispatch) => {
  dispatch({ type: LOADING_WORK_ORDERS });
  try {
    const { data } = await backend.get(`/work-orders/`, {
      params: { execution_brief_id: executionBriefId },
    });
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: LIST_WORK_ORDERS,
      payload: data,
    });
  } catch (error) {
    handleError(error, dispatch, SET_WORK_ORDER_ERRORS);
  }
};

export const getWorkOrder = (id: string): GenericRootThunkAction => async (dispatch) => {
  try {
    dispatch({ type: LOADING_WORK_ORDERS });
    const { data } = await backend.get(`/work-orders/${id}/`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_WORK_ORDER_DATA,
      payload: {
        data,
      },
    });

    const {
      data: { status: executionBriefStatus },
    } = await backend.get(`/execution-briefs/${data.executionBrief}/`);
    dispatch(setWorkOrderParentStatus(executionBriefStatus));
  } catch (error) {
    handleError(error, dispatch, SET_WORK_ORDER_ERRORS, {
      404: { message: "This Work Order does not exist." },
    });
  }
};

export const resetWorkOrderAttributeText = (attributeName: string) => ({
  type: RESET_WORK_ORDER_ATTRIBUTE,
  payload: { attributeName },
});

export const resetWorkOrder = () => ({
  type: RESET_WORK_ORDER,
});

export const saveWorkOrder = (
  { executionBriefId, resourceType }: any,
  history?: any
): GenericRootThunkAction => async (dispatch) => {
  // Save the WorkOrder Brief reducer
  const params = {
    executionBrief: executionBriefId,
    resourcetype: resourceType,
  };
  try {
    dispatch({ type: SET_WORK_ORDER_LOADING, payload: LOADING_STATES.loading });

    const { data } = await backend.post("/work-orders/", params);

    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({ type: SUCCESS_NEW_WORK_ORDER_PAGE, payload: data });
    const { id } = data;
    if (typeof id !== "undefined" && history) {
      history.push(`${PAGES.WORK_ORDER.URL}/${id}`);
    }
  } catch (error) {
    handleError(error, dispatch, FAILURE_NEW_WORK_ORDER_PAGE);
    dispatch(errorMessage(createError(error).errorMessage));
  }
};

const resetWorkOrderStatus = (id: number): GenericRootThunkAction => async (dispatch) => {
  try {
    await backend.put(`work-orders/${id}/reset/`);
    dispatch(setWorkOrderStatus("draft"));
    dispatch(successMessage("Successfully restarted work."));
  } catch (error) {
    handleError(error, dispatch, FAILURE_UPDATE_WORK_ORDER_PAGE);
    dispatch(errorMessage(createError(error).errorMessage));
  }
};

export const updateWorkOrder = (form: WorkOrderProps): GenericRootThunkAction => async (
  dispatch,
  getState
) => {
  const oldStatus = getState().workOrders.data!.status;

  const params = mapFormToParams(form);

  if (
    (oldStatus === "work_can_start" || oldStatus === "ready_for_creative_review") &&
    form.status === "draft"
  ) {
    return dispatch(resetWorkOrderStatus(form.id));
  }

  dispatch({ type: SET_WORK_ORDER_LOADING, payload: LOADING_STATES.loading });

  try {
    if (form.status === "approved" && oldStatus === "ready_for_creative_review") {
      const {
        data: { status: newStatus },
      } = await backend.put(`/work-orders/${form.id}/approve/`);

      if (newStatus) {
        dispatch(setWorkOrderStatus(newStatus));
        return dispatch(successMessage("Successfully approved creative review."));
      } else {
        return dispatch(errorMessage("Something went wrong."));
      }
    }

    const { data } = await backend.patch(`/work-orders/${form.id}/`, params);
    if (!data) {
      throw new Error("No data in network response");
    }

    if (form.status === "work_can_start" && data.status !== "work_can_start") {
      try {
        const {
          data: { status: newStatus },
        } = await backend.put(`/work-orders/${form.id}/start-review/`);

        updateSuccess(dispatch, { ...data, status: newStatus }, "Successfully started work.");
      } catch (err) {
        dispatch(errorMessage("Something went wrong."));
        console.error(err);
      }
    } else if (
      form.status === "ready_for_creative_review" &&
      data.status !== "ready_for_creative_review"
    ) {
      try {
        const {
          data: { status: newStatus },
        } = await backend.put(`/work-orders/${form.id}/ready-for-creative-review/`);

        updateSuccess(dispatch, { ...data, status: newStatus }, "Successfully updated status.");
      } catch (err) {
        dispatch(errorMessage("Something went wrong."));
        console.error(err);
      }
    } else {
      updateSuccess(dispatch, data, "Successfully saved brief work order.");
    }
  } catch (error) {
    handleError(error, dispatch, FAILURE_UPDATE_WORK_ORDER_PAGE);
    dispatch(errorMessage(createError(error).errorMessage));
  }
};

const updateSuccess = (dispatch: any, data: any, message: string) => {
  dispatch({ type: CLEAR_ERRORS });
  dispatch({ type: SUCCESS_UPDATE_WORK_ORDER_PAGE, payload: { ...data } });
  dispatch(successMessage(message));
};

export const getStoreTypeOptions = (templateName?: string): GenericRootThunkAction => async (
  dispatch
) => {
  try {
    const { data } = await backend.get(`/store-type-options/?search=${templateName}`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_STORE_FORMATS_OPTIONS,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    handleError(error, dispatch, SET_STORE_FORMATS_OPTIONS_ERRORS);
  }
};

export const setWorkOrderStatus = (status: ApprovalStatus) => ({
  type: SET_WORK_ORDER_STATUS,
  payload: {
    status,
  },
});

export const setWorkOrderParentStatus = (parentStatus: ApprovalStatus) => ({
  type: SET_WORK_ORDER_PARENT_STATUS,
  payload: {
    parentStatus,
  },
});

export const getFileFormatOptions = (): GenericRootThunkAction => async (dispatch) => {
  try {
    const { data } = await backend.get(`/file-format-options/`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_FILE_FORMAT_OPTIONS,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    handleError(error, dispatch, SET_FILE_FORMAT_OPTIONS_ERRORS);
  }
};

export const showWorkOrderAddProductModal = () => ({ type: SHOW__WORK_ORDER_ADD_PRODUCT_MODAL });

export const hideWorkOrderAddProductModal = () => ({ type: HIDE_WORK_ORDER_ADD_PRODUCT_MODAL });

export const ADD_WORK_ORDERS_PRODUCT = "ADD_WORK_ORDERS_PRODUCT";
export const REMOVE_WORK_ORDERS_PRODUCT = "REMOVE_WORK_ORDERS_PRODUCT";

export const SET_WORK_ORDERS_CURRENT_TAB = "SET_WORK_ORDERS_CURRENT_TAB";

export const SET_ASSET_REQUEST_OPTIONS = "SET_ASSET_REQUEST_OPTIONS";
export const SET_ASSET_REQUEST_OPTIONS_ERRORS = "SET_ASSET_REQUEST_OPTIONS_ERRORS";

export const getAssetRequestOptions = (): GenericRootThunkAction => async (dispatch) => {
  try {
    const { data } = await backend.get(`/asset-request-options/`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_ASSET_REQUEST_OPTIONS,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    handleError(error, dispatch, SET_ASSET_REQUEST_OPTIONS_ERRORS);
  }
};

export const SET_ASSET_TYPE_OPTIONS = "SET_ASSET_TYPE_OPTIONS";
export const SET_ASSET_TYPE_OPTIONS_ERRORS = "SET_ASSET_TYPE_OPTIONS_ERRORS";
export const SET_ASSET_TYPE_DIMENSIONS = "SET_ASSET_TYPE_DIMENSIONS";
export const SET_ASSET_TYPE_DIMENSIONS_ERRORS = "SET_ASSET_TYPE_DIMENSIONS_ERRORS";

export const getAssetTypeOptions = (templateName?: string): GenericRootThunkAction => async (
  dispatch
) => {
  try {
    const { data } = await backend.get(`/asset-type-options/?search=${templateName}`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_ASSET_TYPE_OPTIONS,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    handleError(error, dispatch, SET_ASSET_TYPE_OPTIONS_ERRORS);
  }
};

export const getAssetTypeDimensions = (assetTypeName: string): GenericRootThunkAction => async (
  dispatch
) => {
  try {
    const { data } = await backend.get(`/asset-type-size-dimensions/?search=${assetTypeName}`);
    if (!data) {
      throw new Error("No data in network response");
    }

    dispatch({ type: CLEAR_ERRORS });
    dispatch({
      type: SET_ASSET_TYPE_DIMENSIONS,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    handleError(error, dispatch, SET_ASSET_TYPE_DIMENSIONS_ERRORS);
  }
};
